import React from 'react';
import * as Actions from '@shared/scenes/chat/input/store/actions';
import * as ChatThreadActions from '@shared/scenes/chat/thread/store/actions';
import { useDispatch, useSelector } from '@shared/store';
import { useIsDesktop } from '@shared/util/hooks';
import { MBChatInput as MBChatInputComponent } from '../../components/index';

const emptyAttachments: MBAPI.ChatAttachment[] = [];

interface MBChatInputProps {
  placeholder: string;
  draftId: string;
  users: MBAPI.ShortUser[];
  groups: MBAPI.ChannelGroup[];
  hideUrgent?: boolean;
  // When this flag is set, the message is not sent when
  // the send button is pressed. Instead, onSend will be
  // called with the message that should be sent. This is
  // eg used in new message flow since we might want to
  // create a channel before sending the actuall message.
  preventSendingMessage?: boolean;
  autoFocus?: boolean;
  onSend?(message: MBChatMessageDraft): void;
  onFocus?(): void;
  onBlur?(): void;
  paddingHorizontal?: number;
}

export function MBChatInput({
  placeholder,
  draftId,
  users,
  groups,
  hideUrgent,
  autoFocus,
  onSend,
  onFocus,
  onBlur,
  preventSendingMessage,
  paddingHorizontal,
}: MBChatInputProps) {
  const dispatch = useDispatch();
  const attachments = useSelector(
    state => state.chatInput.attachmentsFromDraftId[draftId] ?? emptyAttachments,
  );
  const urgent = useSelector(state => state.chatInput.urgentFromDraftId[draftId] || false);
  const storedContent = useSelector(state => state.chatInput.contentFromDraftId[draftId]);
  const isDesktop = useIsDesktop();
  const messageBeingEdited = useSelector(
    state => state.chatMessages.messageBeingEditedFromDraftId[draftId],
  );

  const endEditingMessage = React.useCallback(
    () => ChatThreadActions.endEditingMessage(draftId)(dispatch),
    [draftId, dispatch],
  );
  const handleSend = React.useCallback(
    (content: string) => {
      // 1. Reset the store for the given draft
      Actions.disableUrgent(draftId)(dispatch);
      Actions.clearAttachments(draftId)(dispatch);

      if (preventSendingMessage) {
        // 2. If we're eg in the new message flow, we only want to
        // call the onSend callback, instead of adding the message
        // to the send queue.
        onSend?.({ urgent, content, attachments, editing: null });
      } else if (!!messageBeingEdited) {
        // 3. If editing, edit the message
        ChatThreadActions.editMessage({
          content,
          channelSlug: draftId,
          message: messageBeingEdited,
        })(dispatch);
      } else {
        // 4. Otherwise, send the message
        ChatThreadActions.sendMessageFromDraft(
          { urgent, content, attachments, editing: null },
          draftId,
        )(dispatch);

        // 5. Scroll to the bottom of the chat thread
        // Note, we don't do this when editing a message since we don't
        // want to scroll away from the message the user just edited.
        onSend?.({ urgent, content, attachments, editing: null });
      }
    },
    [draftId, urgent, preventSendingMessage, messageBeingEdited, attachments, onSend, dispatch],
  );

  // When the input component unmounts, we store the current content in
  // redux so that we can init the chat input with that value when the
  // user returns to a specific draftId.
  const storeContent = React.useCallback(
    (content: string) => Actions.updateContent(draftId, content)(dispatch),
    [draftId, dispatch],
  );

  return (
    // Why do we set a key to the component? Because we want it to reset
    // when changing chat/starting to edit a message. This will cause
    // initialValue to be set again.
    <MBChatInputComponent
      key={`${messageBeingEdited && messageBeingEdited.id}:${draftId}`}
      isEditing={!!messageBeingEdited}
      initialValue={!!messageBeingEdited ? messageBeingEdited.content : storedContent}
      draftId={draftId}
      autoFocus={autoFocus !== undefined ? autoFocus : isDesktop}
      hideUrgent={hideUrgent}
      placeholder={placeholder}
      users={users}
      groups={groups}
      onSend={handleSend}
      onFocus={onFocus}
      onBlur={onBlur}
      onCancelEdit={endEditingMessage}
      onUnmount={storeContent}
      paddingHorizontal={paddingHorizontal}
    />
  );
}
